import {ProductProps} from '@/molecules/Cards/Product/typings';
import {useEffect, useRef, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import {useDispatch, useSelector} from 'react-redux';
import useMounted from '../../../hooks/use-mounted/use-mounted';
import useUrl from '../../../hooks/use-url/use-url';
import {
  historyChangedEvent as gtmUrlChange,
  historyNewPagesChangedEvent as gtmUrlChangeNew,
  pushGAViewItemListEvent as gtmViewItemList,
} from '../../../utils/gtm';
import ProductCardSkeleton from '../../atoms/Skeletons/ProductCard/ProductCardSkeleton';
import ProductCard from '../../molecules/Cards/Product/ProductCard';
import Pagination from '../../molecules/Pagination/Pagination';
import Filters from '../Filters/Filters';
import MasonryLayout from '../Layout/Masonry/MasonryLayout';
import {StyledCounter, StyledErrorMsg} from './styles';
import Props, {ImpressionProductsProps} from './typings';

// Actions
import {loadProductFavourites} from '../../../../redux/actions/productFavouriteActions';

// States
import AdCard from '@/molecules/Cards/Ad/AdCard';
import {pushGAAddToWishlistEvent, pushGASelectItemEvent} from '@/utils/gtm';
import {decodeSearchText} from '@/utils/helpers/string';
import {encodeSearchTextInParams} from '@/utils/helpers/url';
import React from 'react';
import {setUrlFilters} from '../../../../redux/actions/filterSettingsActions';
import {impressions} from '../../../../redux/actions/trackingActions';
import {STATUS_FAILED, STATUS_SUCCEEDED} from '../../../../redux/actionTypes';
import {selectSearchSettings} from '../../../../redux/reducers/filterSettingsReducer';
import {
  selectGalleryData,
  selectGallerySeedStatus,
} from '../../../../redux/reducers/galleryReducer';
import {selectUserSettingsData} from '../../../../redux/reducers/userSettingsReducer';
import useCookie from '../../../hooks/use-cookie/use-cookie';
import configs from '../../../utils/configs';
import paramsHelper from '../../../utils/paramsHelper';

const Gallery = ({
  itemsPerPage = 60,
  itemsPerCall = itemsPerPage,
  showCounter,
  reduxActions,
  returnUrlParams,
  initData,
  basePath,
  urlParams = '',
  itemsEagerLoadedPerCall = 4,
}: Props) => {
  // Hooks
  const hasMounted = useMounted();
  const dispatch = useDispatch();
  const url = useUrl(basePath);
  const {ref: pagination, inView} = useInView({
    threshold: 0,
  });
  const [shippingCookie] = useCookie('af-shipping', null);

  // Redux selectors
  const userSettings = useSelector(selectUserSettingsData);
  const searchData = useSelector(selectGalleryData);
  const appliedFilters = useSelector(selectSearchSettings); // Filters applied to search
  const seedStatus = useSelector(selectGallerySeedStatus);

  // Variables
  const maxCallsPerPage = itemsPerPage / itemsPerCall;
  const [activePage, setActivePage] = useState(0);
  const [items, setItems] = useState<ProductProps[]>(initData?.searchResult?.results ?? []);
  const loading = useRef(false);
  const [showSkeletons, setShowSkeletons] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const callNumber = useRef(1);
  const paginationInView = useRef(false); // Used since simply inView is not reliable
  const canUrlUpdate = useRef(false);
  const [counterTitle, setCounterTitle] = useState('');
  const controller = useRef<AbortController>();
  const filtersRef = useRef(appliedFilters);
  const firstLoad = useRef(true);
  const cachedCall = useRef<number | null>(null);
  const initParamsCorrect = useRef(true);

  const checkInitParams = (filters) => {
    const userCountry = shippingCookie ?? userSettings.country;

    if (filters.free_shipping && !filters.ships_to) {
      filters.ships_to = userCountry;
      initParamsCorrect.current = false;
    }
    if (filters.ships_to && filters.ships_to !== userCountry) {
      filters.ships_to = userCountry;
      initParamsCorrect.current = false;
    }
    if (!initParamsCorrect.current) canUrlUpdate.current = true;
    return filters;
  };

  // On page load
  useEffect(() => {
    if (!hasMounted) return;

    let onLoadFilters = readUrlFilters();
    if (reduxActions.filters && reduxActions.filters.GA.filterUsed) {
      onLoadFilters = checkInitParams(onLoadFilters);
    }

    // Applies URL filters
    dispatch(setUrlFilters(onLoadFilters));

    // Loads customer product favourites
    dispatch(loadProductFavourites());

    return () => {
      cancelRequest();
    };
  }, [hasMounted]);

  // Page changes
  useEffect(() => {
    if (!hasMounted) return;

    callNumber.current = (activePage - 1) * maxCallsPerPage + 1;
    showNewPageResults();
  }, [activePage]);

  // Update counter title
  useEffect(() => {
    if (!hasMounted || !showCounter || searchData.totalCount === 0) return;
    updateCounterTitle();
  }, [activePage, searchData.totalCount]);

  // Check for pagination being in view
  useEffect(() => {
    if (!hasMounted) return;

    paginationInView.current = inView;
    loadMoreItems();
  }, [inView]);

  // Search result updated
  useEffect(() => {
    if (!hasMounted) return;
    addItems();
  }, [searchData.results]);

  // Applied filters changed
  useEffect(() => {
    if (!hasMounted) return;

    // User changed his country settings during active search
    if (reduxActions.filters) {
      if (filtersRef.current.ships_to && filtersRef.current.ships_to !== appliedFilters.ships_to) {
        canUrlUpdate.current = true;
      }
      filtersRef.current = appliedFilters;
      dispatch(reduxActions.filters.clearSearch());
    }

    // Load seo text
    if (reduxActions.SEO) {
      if (firstLoad.current && initData?.seo) {
        dispatch(reduxActions.SEO.set(initData.seo));
      } else {
        updateSeoText();
      }
    }

    // Call it only if filters are reset on first page
    if (activePage === 1) {
      callNumber.current = 1;
      showNewPageResults();
    }

    // loadedCalls can be 0 only on page load, so read page from initData
    goToPage(firstLoad.current === true ? (initData?.page ?? 1) : 1);
  }, [appliedFilters]);

  useEffect(() => {
    if (seedStatus === STATUS_FAILED) {
      search();
    } else if (seedStatus === STATUS_SUCCEEDED) {
      const currentPageTracked = callNumber.current - 1;

      if (searchData?.results?.[currentPageTracked]) {
        gtmViewItemList(
          searchData.results[currentPageTracked],
          userSettings.currency,
          (currentPageTracked - 1) * itemsPerCall,
        );
      }

      trackImpressions(items, currentPageTracked);

      if (paginationInView.current) setTimeout(loadMoreItems, 500);
    }
  }, [seedStatus]);

  const showNewPageResults = () => {
    if (!firstLoad.current) updateUrl();

    if (firstLoad.current && initParamsCorrect.current) {
      const successCallbacks = [
        (data) => {
          if (data?.results && !data.results.length) setErrorMsg('No results found');
        },
      ];
      const errorCallbacks = [
        () => {
          cachedCall.current = null;
        },
      ];

      if (!!initData?.searchResult?.results) cachedCall.current = callNumber.current; // Check is here because success callback will be executed after Redux is populated, which is too late

      if (reduxActions.seedSearch) {
        dispatch(
          reduxActions.seedSearch(
            initData?.searchResult,
            callNumber.current,
            successCallbacks,
            errorCallbacks,
          ),
        );
      } else {
        search();
      }
    } else {
      cancelRequest();
      setItems([]);
      window.scrollTo({top: 0});
      search();
    }
  };

  // Get initial settings on page load
  const readUrlFilters = () => {
    const filters = url.read();

    // Settings from other sources
    filters['paginate'] = itemsPerPage;
    filters['limit'] = itemsPerCall;
    if (filters['q']) {
      filters['q'] = decodeSearchText(filters['q']);
    }

    return filters;
  };

  const updateUrl = () => {
    const params = paramsHelper(encodeSearchTextInParams(filtersRef.current))
      .build({page: activePage})
      .sort(configs.urlParams).result;

    const urlParams = url.write(params);
    canUrlUpdate.current = false;

    dispatch(reduxActions.GA.urlChanged());
    gtmUrlChange();
    gtmUrlChangeNew();

    if (returnUrlParams) returnUrlParams(urlParams);
  };

  const updateSeoText = () => {
    const filters = paramsHelper(filtersRef.current)
      .build()
      .filter(['paginate', 'limit'])
      .sort(configs.apiParams).result;

    if (reduxActions.SEO?.get) {
      dispatch(reduxActions.SEO.get(filters));
    }
  };

  const updateCounterTitle = () => {
    const startImage = (activePage - 1) * itemsPerPage + 1;
    let endImage = activePage * itemsPerPage;
    endImage = endImage > searchData.totalCount ? searchData.totalCount : endImage;
    setCounterTitle(
      `${startImage} - ${endImage} of ${searchData.totalCount} ${
        searchData.totalCount > 0 ? 'artworks' : 'artwork'
      }`,
    );
  };

  // Check if conditions are met to do additional image load call
  const loadMoreItems = () => {
    if (
      paginationInView.current &&
      !loading.current &&
      callNumber.current <= searchData.totalPageCount &&
      callNumber.current <= maxCallsPerPage * activePage
    ) {
      search();
    }
  };

  const setLoading = (state) => {
    setShowSkeletons(state);
    loading.current = state;
  };

  const addItems = () => {
    if (firstLoad.current) {
      firstLoad.current = false;
      if (callNumber.current === cachedCall.current) callNumber.current++;
    }

    if (!!Object.keys(searchData.results).length && !!searchData.results[callNumber.current]) {
      if (callNumber.current % maxCallsPerPage === 1) {
        setItems(searchData.results[callNumber.current]);
        callNumber.current++;
      } else {
        setItems([...items, ...searchData.results[callNumber.current]]);
        callNumber.current++;
      }
    }
  };

  const additionalSearchParams = () => {
    // Base
    const params = {
      page: callNumber.current,
    };

    // Check if we need to send units
    if (
      appliedFilters?.size?.width_min ||
      appliedFilters?.size?.width_max ||
      appliedFilters?.size?.height_min ||
      appliedFilters?.size?.height_max
    )
      params['user_units'] = userSettings.display_units;

    // Check if we need to send currency
    if (appliedFilters?.price?.price_min || appliedFilters?.price?.price_max)
      params['user_currency'] = userSettings.currency;

    return params;
  };

  const search = () => {
    if (loading.current || !reduxActions.filters?.search) return;
    if (errorMsg) setErrorMsg(null);

    controller.current = new AbortController();

    setLoading(true);

    const apiParams = paramsHelper(filtersRef.current)
      .build(additionalSearchParams())
      .filter(['paginate'])
      .unwrapCategories()
      .sort(configs.apiParams).result;
    const currentPageTracked = callNumber.current;

    const successCallbacks = [
      (data) => {
        setLoading(false);

        if (data.results.length) {
          gtmViewItemList(
            data.results,
            userSettings.currency,
            (currentPageTracked - 1) * itemsPerCall,
          );

          trackImpressions(data.results, currentPageTracked);

          if (paginationInView.current) setTimeout(loadMoreItems, 500);
        } else {
          setErrorMsg('No results found');
        }
      },
    ];

    const finalCallbacks = [];

    const errorCallbacks = [
      (error) => {
        setLoading(false);

        if (error.message === 'canceled') {
          // If nothing was fetched yet, do new search
          search();
          return;
        }
        if (items.length === 0) {
          setErrorMsg('An error occurred, please refresh the page');
        }
      },
    ];

    dispatch(
      reduxActions.filters.search(
        apiParams,
        successCallbacks,
        errorCallbacks,
        finalCallbacks,
        controller.current.signal,
      ),
    );
  };

  const trackImpressions = (products, callNumber: number) => {
    if (!products || !Array.isArray(products)) return;

    const firstCallOnPage = callNumber % maxCallsPerPage === 1;
    const currentPage = Math.ceil(callNumber / maxCallsPerPage);
    const urlArray = `${window.location.pathname + window.location.hash}`.split('page-');
    let finalUrl = urlArray[0];
    if (currentPage > 1) {
      finalUrl = `${finalUrl}page-${currentPage}/`;
    }
    const productsToTrack: ImpressionProductsProps[] = products.map((product) => ({
      above_fold: firstCallOnPage,
      product_slug: product.slug,
      y_pos: 0,
    }));
    dispatch(impressions(finalUrl, productsToTrack));
  };

  const cancelRequest = () => {
    if (loading.current && controller.current) controller.current.abort();
  };

  const goToPage = (page) => {
    setActivePage(page);
  };

  const paginationClick = (page) => {
    dispatch(reduxActions.GA.paginationUsed());
    goToPage(page);
    canUrlUpdate.current = true;
  };

  return (
    <>
      {reduxActions.filters && (
        <Filters
          sort={reduxActions.filters.GA.sortingUsed ? true : undefined}
          filters={reduxActions.filters.GA.filterUsed ? true : undefined}
          reduxActions={reduxActions.filters}
          userSettings={{
            country: userSettings.country,
            currencySymbol: userSettings.currency_symbol,
            displayUnits: userSettings.display_units,
          }}
          searchPreviewCount={searchData.previewCount}
          filtersCallback={() => {
            canUrlUpdate.current = true;
          }}
          sortCallback={() => {
            canUrlUpdate.current = true;
          }}
        />
      )}

      {!!showCounter && searchData.totalCount > 0 && (
        <StyledCounter $shift={reduxActions.filters?.GA.filterUsed ? undefined : true}>
          <p>{counterTitle}</p>
        </StyledCounter>
      )}

      {errorMsg ? (
        <StyledErrorMsg>{errorMsg}</StyledErrorMsg>
      ) : (
        <>
          <MasonryLayout loading={showSkeletons} skeleton={ProductCardSkeleton}>
            {items.map((item, key) => (
              <React.Fragment key={item.id}>
                {initData?.adCards
                  ?.filter((ad) => ad.position === key + 1)
                  .map((ad) => (
                    <AdCard key={ad.id} card={ad} />
                  ))}

                <ProductCard
                  product={item}
                  displaySizeUnits={userSettings.display_units}
                  displayCurrency={userSettings.currency}
                  currencySymbol={userSettings.currency_symbol}
                  lazyLoading={key >= itemsEagerLoadedPerCall}
                  gaEvents={{
                    click: () => {
                      // GA tracking relevant to Gallery Product click
                      pushGASelectItemEvent(item, userSettings.currency);
                    },
                    favourite: () => {
                      // GA tracking relevant to Gallery Product favourite
                      pushGAAddToWishlistEvent(item, userSettings.currency);
                    },
                  }}
                />
              </React.Fragment>
            ))}
          </MasonryLayout>

          {((initData?.searchResult?.count && initData.searchResult.count > 0) ||
            searchData.totalCount > 0) && (
            <div ref={pagination}>
              <Pagination
                activePage={activePage === 0 ? (initData?.page ? initData.page : 1) : activePage}
                paginate={itemsPerPage}
                totalCount={searchData.totalCount ?? initData?.searchResult?.count}
                onClick={paginationClick}
                basePath={basePath}
                urlParams={urlParams}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Gallery;
